import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  KeySquare,
  CheckCircle2,
  Info,
  Save,
  Zap,
  XCircle,
  Copy,
  LinkIcon,
  Unlink,
  ExternalLink,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ store_url: "", client_id: "", client_secret: "", storefront_url: "" });
  const [meta, setMeta] = useState({
    has_secret: false,
    updated_at: null,
    updated_by: null,
    last_tested_at: null,
    last_test_ok: null,
    redirect_uri: "",
    connected: false,
    connected_at: null,
    storefront_url: null,
  });
  const [testResult, setTestResult] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/ideasoft");
      setForm((f) => ({
        ...f,
        store_url: data.store_url || "",
        client_id: data.client_id || "",
        storefront_url: data.storefront_url || "",
      }));
      setMeta({
        has_secret: !!data.has_secret,
        updated_at: data.updated_at,
        updated_by: data.updated_by,
        last_tested_at: data.last_tested_at,
        last_test_ok: data.last_test_ok,
        redirect_uri: data.redirect_uri || "",
        connected: !!data.connected,
        connected_at: data.connected_at,
        storefront_url: data.storefront_url,
      });
    } catch (e) {
      toast.error("Ayarlar okunamadı", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  // Handle ?oauth=ok/error from callback redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauth = params.get("oauth");
    if (oauth === "ok") {
      toast.success("IdeaSoft'a bağlandı");
      navigate("/settings", { replace: true });
      load();
    } else if (oauth === "error") {
      toast.error("Bağlantı başarısız", { description: params.get("msg") || "" });
      navigate("/settings", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_secret) {
      toast.error("Client Secret girmelisiniz");
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      const { data } = await api.put("/settings/ideasoft", {
        store_url: form.store_url.trim(),
        client_id: form.client_id.trim(),
        client_secret: form.client_secret,
        storefront_url: form.storefront_url.trim() || null,
      });
      setMeta({
        has_secret: !!data.has_secret,
        updated_at: data.updated_at,
        updated_by: data.updated_by,
        last_tested_at: data.last_tested_at,
        last_test_ok: data.last_test_ok,
        redirect_uri: data.redirect_uri || "",
        connected: !!data.connected,
        connected_at: data.connected_at,
        storefront_url: data.storefront_url,
      });
      setForm((f) => ({ ...f, client_secret: "" }));
      toast.success("API bilgileri kaydedildi", {
        description: "Şimdi 'Bağlantıyı test et' veya 'IdeaSoft'a Bağlan'.",
      });
    } catch (e) {
      toast.error("Kaydedilemedi", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/ideasoft/test");
      if (data.ok) {
        setTestResult({ ok: true, ...data });
        toast.success("Bağlantı çalışıyor");
      } else {
        setTestResult({ ok: false, error: data.error || "Bilinmeyen hata" });
        toast.error("Bağlantı çalışmıyor", { description: data.error });
      }
      load();
    } catch (e) {
      const err = formatApiError(e);
      setTestResult({ ok: false, error: err });
      toast.error("Bağlantı çalışmıyor", { description: err });
      load();
    } finally {
      setTesting(false);
    }
  };

  const startAuth = async () => {
    try {
      const { data } = await api.get("/oauth/ideasoft/authorize");
      if (!data.ok || !data.authorize_url) {
        toast.error("Yetkilendirme başlatılamadı", { description: data.error || "" });
        return;
      }
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error("Yetkilendirme başlatılamadı", { description: formatApiError(e) });
    }
  };

  const disconnect = async () => {
    if (!window.confirm("IdeaSoft bağlantısını kesmek istiyor musunuz? Kayıtlı token silinecek.")) return;
    try {
      await api.post("/oauth/ideasoft/disconnect");
      toast.success("Bağlantı kesildi");
      load();
    } catch (e) {
      toast.error("Kesilemedi", { description: formatApiError(e) });
    }
  };

  const copyRedirect = async () => {
    if (!meta.redirect_uri) return;
    try {
      await navigator.clipboard.writeText(meta.redirect_uri);
      toast.success("Yönlendirme adresi kopyalandı");
    } catch (_) {
      toast.error("Kopyalanamadı");
    }
  };

  return (
    <div>
      <PageHeader subtitle="IdeaSoft Admin API entegrasyonu" title="API Ayarları" actions={null} />

      <div className="max-w-[900px] mx-auto px-6 py-8 space-y-6">
        {/* Redirect URI card */}
        <div className="border border-[#002FA7] bg-white p-6" data-testid="redirect-card">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-sm bg-[#002FA7] text-white flex items-center justify-center shrink-0">
              <LinkIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-black tracking-tight">
                Yönlendirme adresi (Redirect URI)
              </div>
              <div className="text-sm text-zinc-500 mt-1">
                IdeaSoft&apos;taki uygulama formundaki <span className="font-mono">Yönlendirme Adresi</span>{" "}
                alanına <b>aynen</b> yapıştırın.
              </div>
              <div className="mt-3 flex items-stretch gap-2">
                <div
                  className="flex-1 border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono text-xs break-all min-h-[38px] flex items-center"
                  data-testid="redirect-uri-value"
                >
                  {meta.redirect_uri || "—"}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-sm border-zinc-300 shrink-0"
                  onClick={copyRedirect}
                  disabled={!meta.redirect_uri}
                  data-testid="copy-redirect-btn"
                >
                  <Copy className="h-4 w-4 mr-1.5" /> Kopyala
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Credentials card */}
        <div className="border border-zinc-200 bg-white p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-zinc-950 text-white flex items-center justify-center">
              <KeySquare className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-black tracking-tight">API Bilgileri</div>
              <div className="text-sm text-zinc-500 mt-1 max-w-lg">
                Client Secret Fernet ile şifrelenmiş olarak saklanır, bir daha gösterilmez.
              </div>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-sm" />
              ))}
            </div>
          ) : (
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">Mağaza URL</Label>
                <Input
                  value={form.store_url}
                  onChange={(e) => setForm({ ...form, store_url: e.target.value })}
                  placeholder="magazam.myideasoft.com"
                  className="rounded-sm border-zinc-300 mt-1 font-mono"
                  required
                  data-testid="settings-store-url"
                />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">Client ID</Label>
                <Input
                  value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  className="rounded-sm border-zinc-300 mt-1 font-mono"
                  required
                  data-testid="settings-client-id"
                />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 flex items-center gap-1">
                  Client Secret
                  {meta.has_secret ? (
                    <span className="text-emerald-600 flex items-center gap-1 text-[9px]">
                      <CheckCircle2 className="h-3 w-3" /> kayıtlı
                    </span>
                  ) : null}
                </Label>
                <Input
                  type="password"
                  value={form.client_secret}
                  onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                  className="rounded-sm border-zinc-300 mt-1 font-mono"
                  placeholder={meta.has_secret ? "Değiştirmek için yeni sırrı girin" : ""}
                  required
                  data-testid="settings-client-secret"
                />
              </div>

              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                  Mağaza Adresi (Müşteri Link'i için) <span className="text-zinc-400 normal-case">— opsiyonel</span>
                </Label>
                <Input
                  type="text"
                  value={form.storefront_url}
                  onChange={(e) => setForm({ ...form, storefront_url: e.target.value })}
                  className="rounded-sm border-zinc-300 mt-1 font-mono"
                  placeholder="www.magazam.com"
                  data-testid="settings-storefront-url"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  Boşsa müşteri linki üretilemez. Taslak siparişten sonra kullanıcıya gönderilecek
                  linkin domain'i (ör. <span className="font-mono">www.ofiseal.com</span>) —
                  API adresinden ({" "}
                  <span className="font-mono">*.myideasoft.com</span>) farklı olabilir.
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button
                  type="submit"
                  className="rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white h-10"
                  disabled={saving}
                  data-testid="settings-save-btn"
                >
                  <Save className="h-4 w-4 mr-1.5" />
                  {saving ? "Kaydediliyor…" : "Kaydet"}
                </Button>
              </div>

              {meta.updated_at ? (
                <div className="text-[11px] font-mono text-zinc-500 pt-2 border-t border-zinc-200">
                  Son güncelleme: {formatDate(meta.updated_at)} · {meta.updated_by}
                </div>
              ) : null}
            </form>
          )}
        </div>

        {/* Connection / Test card */}
        <div className="border border-zinc-200 bg-white p-6" data-testid="connection-card">
          <div className="flex items-start gap-3 mb-4">
            <div
              className={[
                "h-10 w-10 rounded-sm text-white flex items-center justify-center shrink-0",
                meta.connected ? "bg-emerald-600" : "bg-zinc-400",
              ].join(" ")}
            >
              {meta.connected ? <CheckCircle2 className="h-5 w-5" /> : <Unlink className="h-5 w-5" />}
            </div>
            <div className="flex-1">
              <div className="text-lg font-black tracking-tight">
                {meta.connected ? "Bağlı" : "Bağlanılmadı"}
              </div>
              <div className="text-sm text-zinc-500 mt-1">
                {meta.connected
                  ? "IdeaSoft'a bağlı — access + refresh token güvenli şekilde saklanıyor."
                  : "IdeaSoft uygulaman authorization_code destekliyorsa 'IdeaSoft'a Bağlan' butonuyla; sadece client_credentials destekliyorsa 'Bağlantıyı test et' butonuyla ilerleyebilirsin."}
              </div>
              {meta.connected && meta.connected_at ? (
                <div className="text-[11px] font-mono text-zinc-500 mt-2">
                  Bağlanma zamanı: {formatDate(meta.connected_at)}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white h-10"
              disabled={!meta.has_secret}
              onClick={startAuth}
              data-testid="settings-connect-btn"
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              IdeaSoft&apos;a Bağlan
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-zinc-300 h-10"
              disabled={testing || !meta.has_secret}
              onClick={test}
              data-testid="settings-test-btn"
            >
              <Zap className="h-4 w-4 mr-1.5" />
              {testing ? "Deneniyor…" : "Bağlantıyı test et"}
            </Button>
            {meta.connected ? (
              <Button
                type="button"
                variant="outline"
                className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 h-10"
                onClick={disconnect}
                data-testid="settings-disconnect-btn"
              >
                <Unlink className="h-4 w-4 mr-1.5" />
                Bağlantıyı Kes
              </Button>
            ) : null}
          </div>

          {meta.last_tested_at ? (
            <div
              className={[
                "mt-3 text-[11px] font-mono flex items-center gap-1.5",
                meta.last_test_ok ? "text-emerald-700" : "text-red-700",
              ].join(" ")}
              data-testid="last-test-status"
            >
              {meta.last_test_ok ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <XCircle className="h-3.5 w-3.5" />
              )}
              Son test: {formatDate(meta.last_tested_at)} —{" "}
              {meta.last_test_ok ? "başarılı" : "başarısız"}
            </div>
          ) : null}

          {testResult ? (
            <div
              className={[
                "mt-3 rounded-sm border p-3 text-xs",
                testResult.ok
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-red-50 border-red-200 text-red-800",
              ].join(" ")}
              data-testid="settings-test-result"
            >
              {testResult.ok ? (
                <>
                  Bağlantı çalışıyor — <span className="font-mono">{testResult.base}</span>{" "}
                  · örnek {testResult.sample_count} sepet · token{" "}
                  {testResult.token_expires_in}s
                </>
              ) : (
                <>Hata: {testResult.error}</>
              )}
            </div>
          ) : null}
        </div>

        {/* Guide */}
        <div className="border border-zinc-200 bg-zinc-50 p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Nasıl bağlarım?
          </div>
          <ol className="mt-2 text-sm text-zinc-700 space-y-1.5 list-decimal ml-5">
            <li>
              IdeaSoft yönetim paneli → Uygulama oluştur → <b>Yönlendirme Adresi</b>
              &apos;ne yukarıdaki mavi kutuda gördüğünüz URL&apos;yi <b>aynen</b> yapıştırın.
            </li>
            <li>
              <b>İzin Yönetimi</b>&apos;nden <span className="font-mono">activeCart (okuma)</span>{" "}
              ve <span className="font-mono">draftOrder (okuma+yazma)</span> yetkilerini verin.
            </li>
            <li>
              Üretilen <b>Client ID</b> ve <b>Client Secret</b>&apos;i buradaki forma yapıştırıp{" "}
              <b>Kaydet</b>&apos;e basın.
            </li>
            <li>
              <b>IdeaSoft&apos;a Bağlan</b>&apos;a basın. IdeaSoft admin girişi + &quot;İzin
              Ver&quot; ekranından sonra otomatik geri dönersiniz. &quot;Bağlı&quot; yeşil olur.
            </li>
            <li>
              Alternatif: uygulaman client_credentials destekliyorsa &quot;Bağlantıyı test et&quot;
              butonuyla Bağlan adımını atlayabilirsin.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
